import {
    MAP_GROUP_ID_TO_SALESCHANNEL,
    SAILS_BNT_STORAGE,
    SAILS_PARAM_SALESCHANNEL,
    SAILS_PARAM_SUB_ID,
    SALESCHANNEL_CONSUMER,
    URL_PARAM_SUBGROUP_ID,
    MAP_SALESCHANNEL_TO_GROUP_ID,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_REDPLUS,
    REDPLUS_STORAGE,
    REDPLUS_ALLNET_GROUP,
    SAILS_PARAM_GIGAKOMBI,
    SAILS_PARAM_RLZ,
    RedTariff,
    YoungTariff,
    RedplusTariff,
} from '@vfde-fe/sails-constants';
import {
    getRestlaufzeitFromSails,
    RedPlusStorage,
} from '@vfde-fe/sails-storage';
import {
    getSessionStorageItemJson,
    getURLParam,
} from '@vfde-fe/sails_core';
import {
    APP,
    CONTAINER_TARIFF,
} from 'Constant';
import {
    GIGAKOMBI_TOKEN_PARAM,
    GIGAKOMBI_URL_PARAM,
} from '../../DeviceDetail/constants';
import {
    SailsHardwareDetailsStorage,
} from '../../DeviceDetail/interfaces/storage';
import { IInitialState } from '../interfaces/state';

/**
 * Get the subscription ID from URL or session storage and validate against saleschannel
 */
export const getSubscriptionId = (salesChannel: IInitialState['salesChannel'] = SALESCHANNEL_CONSUMER): RedTariff | YoungTariff | RedplusTariff | null => {
    // Default subscription id
    let subscriptionId = getSubscriptionFromUrl(salesChannel) ?? getSubscriptionFromStorage();

    // Redplus subscription id
    if (salesChannel === SALESCHANNEL_REDPLUS) {
        subscriptionId = getSubscriptionFromUrl(SALESCHANNEL_REDPLUS) ?? getSubscriptionFromRedplusStorage() ?? getSubscriptionFromStorage();
    }

    if (subscriptionId && validateSubscriptionId(subscriptionId, salesChannel!)) {
        return subscriptionId;
    }

    // For redplus: if no valid subscriptionId, or subscriptionId is not redplus, we return redplus allnet as default
    if (salesChannel === SALESCHANNEL_REDPLUS) {
        return REDPLUS_ALLNET_GROUP;
    }

    return null;
};

/**
 * Get subscription ID from URL
 */
export const getSubscriptionFromUrl = (salesChannel?: IInitialState['salesChannel']): RedTariff | YoungTariff | RedplusTariff | null => {
    const urlParamSubGroupId = getURLParam(URL_PARAM_SUBGROUP_ID) as RedTariff | YoungTariff | RedplusTariff;

    if (salesChannel && urlParamSubGroupId && urlParamSubGroupId in MAP_GROUP_ID_TO_SALESCHANNEL && MAP_GROUP_ID_TO_SALESCHANNEL[urlParamSubGroupId].indexOf(salesChannel) !== -1) {
        return urlParamSubGroupId;
    }

    if (!salesChannel && urlParamSubGroupId && urlParamSubGroupId in MAP_GROUP_ID_TO_SALESCHANNEL) {
        return urlParamSubGroupId;
    }

    return null;
};

/**
 * Get subscription ID from storage
 */
export const getSubscriptionFromStorage = (): RedTariff | YoungTariff | RedplusTariff | undefined => {
    const storage = getSessionStorageItemJson<SailsHardwareDetailsStorage | null>(SAILS_BNT_STORAGE);
    const salesChannel = storage?.[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_SALESCHANNEL];

    return (salesChannel && storage[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_SUB_ID]?.[salesChannel]) ?? storage?.[SAILS_PARAM_SUB_ID]?.[storage[SAILS_PARAM_SALESCHANNEL]];
};

/**
 * Get Redplus subscription ID from storage
 */
export const getSubscriptionFromRedplusStorage = (): RedplusTariff | undefined => {
    const storage = getSessionStorageItemJson<RedPlusStorage | null>(REDPLUS_STORAGE);

    return storage?.[SAILS_PARAM_SUB_ID]?.toString() as RedplusTariff;
};

/**
 * Get the saleschannel from given subscription ID. Returns default "consumer".
 */
export const getSaleschannel = (): IInitialState['salesChannel'] => {
    return getSalesChannelFromUrl() ?? getSalesChannelFromStorage() ?? getSalesChannelFromSubscription() ?? SALESCHANNEL_CONSUMER;
};

/**
 * Get Saleschannel from URL
 */
export const getSalesChannelFromUrl = (): IInitialState['salesChannel'] | null => {
    const salesChannelFromUrl = getURLParam(SAILS_PARAM_SALESCHANNEL);

    if (Object.keys(MAP_SALESCHANNEL_TO_GROUP_ID).indexOf(salesChannelFromUrl) !== -1) {
        return salesChannelFromUrl as IInitialState['salesChannel'];
    }

    return null;
};

/**
 * Get saleschannel from storage
 */
export const getSalesChannelFromStorage = (): IInitialState['salesChannel'] => {
    const storage = getSessionStorageItemJson<SailsHardwareDetailsStorage | null>(SAILS_BNT_STORAGE);

    return (storage?.[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_SALESCHANNEL] ?? storage?.[SAILS_PARAM_SALESCHANNEL]) as IInitialState['salesChannel'];
};

/**
 * Get sales channel from subscription ID
 */
export const getSalesChannelFromSubscription = (): IInitialState['salesChannel'] | null => {
    const subscriptionFromUrl = getSubscriptionFromUrl() as RedTariff | YoungTariff | RedplusTariff;

    if (!subscriptionFromUrl) {
        return null;
    }

    return MAP_GROUP_ID_TO_SALESCHANNEL[subscriptionFromUrl]?.[0] as IInitialState['salesChannel'];
};

/**
 * Get gigakombi from session storage
 */
export const getGigakombi = (salesChannel: string): boolean => {
    if (salesChannel === SALESCHANNEL_FAMILY_FRIENDS || salesChannel === SALESCHANNEL_REDPLUS) {
        return false;
    }

    const storage = getSessionStorageItemJson<SailsHardwareDetailsStorage | null>(SAILS_BNT_STORAGE);

    return getGigakombiFromUrl() ?? getGigakombiFromStorage(storage) ?? false;
};

const getGigakombiFromUrl = (): true | undefined => {
    const gigakombiParam = getURLParam(GIGAKOMBI_URL_PARAM);

    return (gigakombiParam === '1' || gigakombiParam === 'true' || undefined) ?? (!!getURLParam(GIGAKOMBI_TOKEN_PARAM) || undefined);
};

const getGigakombiFromStorage = (storage: SailsHardwareDetailsStorage | null): boolean => {
    return (storage?.[SAILS_PARAM_GIGAKOMBI] ?? storage?.[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_GIGAKOMBI]) || false;
};

/**
 * Validate subscription ID
 * @param id
 * @param salesChannel
 */
export const validateSubscriptionId = (id: RedTariff | YoungTariff | RedplusTariff, salesChannel: Exclude<IInitialState['salesChannel'], null>): boolean => {
    return MAP_SALESCHANNEL_TO_GROUP_ID[salesChannel].indexOf(id) !== -1;
};

/**
 * Get eligibility token
 */
export const getGigakombiEligibilityToken = (): string | null | undefined => {
    return getURLParam(GIGAKOMBI_TOKEN_PARAM);
};

/**
 * Get Restlaufzeit from storage
 * @returns boolean
 */
export const getRestlaufzeit = (): boolean => {
    const storage = getSessionStorageItemJson<SailsHardwareDetailsStorage>(SAILS_BNT_STORAGE);

    return getRestlaufzeitFromSails(storage || {});
};
